import React, { useCallback, useDeferredValue, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import {
  FileSearch,
  Pencil,
  Plus,
  Save,
  Search,
  Trash2,
} from 'lucide-react';
import ExportButton from '../../components/common/ExportButton';
import FilterSelect from '../../components/common/FilterSelect';
import ConfirmModal from '../../components/common/ConfirmModal';
import SearchableProductInput from '../../components/common/SearchableProductInput';
import { productReturnsAPI, productsAPI } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { useStoreContextStore } from '../../store/storeContextStore';
import {
  buildTransferName,
  createReturnRow,
  exportProductReturnEntry,
  formatProductNamesSummary,
} from '../../utils/productReturns';

const todayKey = () => new Date().toISOString().split('T')[0];
const shiftDate = (dateKey, days) => {
  const date = new Date(`${dateKey}T00:00:00`);
  if (Number.isNaN(date.getTime())) return todayKey();
  date.setDate(date.getDate() + days);
  return date.toISOString().split('T')[0];
};
const defaultSavedEntriesFromDate = () => shiftDate(todayKey(), -6);

const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'Completed', label: 'Completed' },
  { value: 'Incomplete', label: 'Incomplete' },
];

const REASON_OPTIONS = [
  { value: 'Damaged', label: 'Damaged' },
  { value: 'Near Expiry', label: 'Near Expiry' },
  { value: 'Expired', label: 'Expired' },
];

const needsDateFields = (reason) => reason === 'Near Expiry' || reason === 'Expired';
const normalizeText = (value) => String(value ?? '').trim();
const toPositiveInteger = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? Math.round(numeric) : 0;
};
const toRowFromItem = (item = {}) => ({
  id: item.id || createReturnRow().id,
  productInput: normalizeText(item.productName),
  productId: normalizeText(item.productId),
  productName: normalizeText(item.productName),
  sku: normalizeText(item.sku),
  quantity: String(toPositiveInteger(item.quantity) || ''),
  reason: normalizeText(item.reason) || 'Damaged',
  manufacturingDate: normalizeText(item.manufacturingDate),
  expiryDate: normalizeText(item.expiryDate),
});
const isConfirmedStatus = (status) => normalizeText(status).toLowerCase() === 'completed';
const getDisplayStatus = (status) => (isConfirmedStatus(status) ? 'Confirmed' : 'Saved');
const isEditLocked = (record = {}) => (
  isConfirmedStatus(record.status)
  && (
    Boolean(record.editControl?.confirmedLocked)
    || !record.editControl?.unlockedAt
  )
);

const ProductReturnsTask = () => {
  const { user, hasFeatureAccess } = useAuthStore();
  const selectedStore = useStoreContextStore((state) => state.selectedStore);
  const selectedStoreContext = useMemo(() => ({
    storeInfoId: selectedStore?.storeInfoId || selectedStore?.store_info_id || null,
    vendorId: selectedStore?.vendorId || selectedStore?.vendor_id || null,
    legacyStoreId: selectedStore?.legacyStoreId || selectedStore?.legacy_store_id || null,
    storeName: selectedStore?.name || selectedStore?.storeName || selectedStore?.storeCode || '',
    storeCode: selectedStore?.storeCode || selectedStore?.store_code || selectedStore?.code || '',
    branchCode: selectedStore?.branchCode || selectedStore?.branch_code || '',
  }), [selectedStore]);
  const hasStoreContext = Boolean(selectedStoreContext.storeInfoId && selectedStoreContext.vendorId);
  const canViewPage = hasFeatureAccess('product_returns.task');
  const [sellerName, setSellerName] = useState('');
  const [rows, setRows] = useState([createReturnRow()]);
  const [records, setRecords] = useState([]);
  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search);
  const [statusFilter, setStatusFilter] = useState('all');
  const [savedFromDate, setSavedFromDate] = useState(defaultSavedEntriesFromDate);
  const [savedToDate, setSavedToDate] = useState(todayKey);
  const [products, setProducts] = useState([]);
  const [sellerOptions, setSellerOptions] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [editingRecordId, setEditingRecordId] = useState('');
  const [recordPendingDelete, setRecordPendingDelete] = useState(null);
  const [deletingRecordId, setDeletingRecordId] = useState('');
  const [errors, setErrors] = useState({});

  const loadRecords = useCallback(async () => {
    if (!canViewPage || !hasStoreContext) {
      setRecords([]);
      return;
    }

    setLoadingRecords(true);
    try {
      const response = await productReturnsAPI.getAll({
        limit: 300,
        fromDate: savedFromDate,
        toDate: savedToDate,
        search: normalizeText(deferredSearch) || undefined,
        status: statusFilter,
      });
      setRecords(response.data?.data || []);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to load product return entries');
    } finally {
      setLoadingRecords(false);
    }
  }, [canViewPage, deferredSearch, hasStoreContext, savedFromDate, savedToDate, statusFilter]);

  useEffect(() => {
    if (!canViewPage) return undefined;

    loadRecords();
    window.addEventListener('focus', loadRecords);

    return () => {
      window.removeEventListener('focus', loadRecords);
    };
  }, [canViewPage, loadRecords, selectedStoreContext.storeInfoId, selectedStoreContext.vendorId]);

  useEffect(() => {
    if (!canViewPage || !hasStoreContext) {
      setProducts([]);
      setSellerOptions([]);
      return;
    }

    const loadCatalog = async () => {
      setLoadingCatalog(true);
      try {
        const response = await productsAPI.getAllCached({
          limit: 2000,
          isActive: 'true',
          storeInfoId: selectedStoreContext.storeInfoId,
          vendorId: selectedStoreContext.vendorId,
        });
        const nextProducts = (response.data?.data || [])
          .map((product) => {
            const name = normalizeText(product.name || product.product);
            const sku = normalizeText(product.sku);
            const seller = normalizeText(product.seller_name || product.sellerName || product.seller);
            return {
              id: product.id,
              name,
              sku,
              seller,
              label: sku ? `${name} (${sku})` : name,
            };
          })
          .filter((product) => product.name)
          .sort((left, right) => left.label.localeCompare(right.label));

        setProducts(nextProducts);
        setSellerOptions(
          Array.from(new Set(nextProducts.map((product) => product.seller).filter(Boolean)))
            .sort((left, right) => left.localeCompare(right))
        );
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Failed to load products');
      } finally {
        setLoadingCatalog(false);
      }
    };

    loadCatalog();
  }, [canViewPage, hasStoreContext, selectedStoreContext.storeInfoId, selectedStoreContext.vendorId]);

  const currentEditingRecord = useMemo(
    () => records.find((record) => record.id === editingRecordId) || null,
    [editingRecordId, records]
  );

  const transferName = useMemo(
    () => buildTransferName({
      storeName: selectedStoreContext.storeName,
      storeCode: selectedStoreContext.storeCode,
      branchCode: selectedStoreContext.branchCode,
      vendorId: selectedStoreContext.vendorId,
      sellerName,
      entries: records,
      currentEntry: currentEditingRecord,
    }),
    [
      currentEditingRecord,
      records,
      sellerName,
      selectedStoreContext.branchCode,
      selectedStoreContext.storeCode,
      selectedStoreContext.storeName,
      selectedStoreContext.vendorId,
    ]
  );

  const filteredProducts = useMemo(() => (
    sellerName
      ? products.filter((product) => normalizeText(product.seller) === normalizeText(sellerName))
      : products
  ), [products, sellerName]);

  const productsLookup = useMemo(() => {
    const map = new Map();
    filteredProducts.forEach((product) => {
      map.set(product.label.toLowerCase(), product);
      map.set(product.name.toLowerCase(), product);
      if (product.sku) {
        map.set(product.sku.toLowerCase(), product);
      }
    });
    return map;
  }, [filteredProducts]);

  const skuCount = useMemo(
    () => rows.filter((row) => normalizeText(row.productInput || row.productName)).length,
    [rows]
  );

  const totalQuantity = useMemo(
    () => rows.reduce((sum, row) => sum + toPositiveInteger(row.quantity), 0),
    [rows]
  );

  const filteredLogs = useMemo(
    () => [...records].sort(
      (left, right) => new Date(right.updatedAt || right.timestamp || right.createdAt || 0).getTime()
        - new Date(left.updatedAt || left.timestamp || left.createdAt || 0).getTime()
    ),
    [records]
  );

  const syncRowProduct = (rowId, inputValue) => {
    setRows((current) => current.map((row) => {
      if (row.id !== rowId) return row;
      const match = productsLookup.get(normalizeText(inputValue).toLowerCase());
      return {
        ...row,
        productInput: inputValue,
        productId: match?.id || '',
        productName: match?.name || normalizeText(inputValue),
        sku: match?.sku || '',
      };
    }));
  };

  const updateRow = (rowId, patch) => {
    setRows((current) => current.map((row) => (
      row.id === rowId ? { ...row, ...patch } : row
    )));
  };

  const selectRowProduct = (rowId, product) => {
    setRows((current) => current.map((row) => (
      row.id === rowId
        ? {
            ...row,
            productInput: product?.name || '',
            productId: product?.id || '',
            productName: product?.name || '',
            sku: product?.sku || '',
          }
        : row
    )));
  };

  const addRow = () => setRows((current) => [...current, createReturnRow()]);

  const removeRow = (rowId) => {
    setRows((current) => (
      current.length === 1 ? [createReturnRow()] : current.filter((row) => row.id !== rowId)
    ));
  };

  const resetForm = () => {
    setSellerName('');
    setRows([createReturnRow()]);
    setEditingRecordId('');
    setErrors({});
  };

  const buildPayload = () => {
    const nextErrors = {};
    const trimmedStoreInfoId = normalizeText(selectedStoreContext.storeInfoId);
    const trimmedVendorId = normalizeText(selectedStoreContext.vendorId);
    const trimmedStore = normalizeText(selectedStoreContext.storeName);
    const trimmedSeller = normalizeText(sellerName);
    const items = rows.map((row, index) => {
      const normalizedReason = normalizeText(row.reason) || 'Damaged';
      const quantity = toPositiveInteger(row.quantity);
      const normalizedProduct = normalizeText(row.productName || row.productInput);
      const itemErrors = {};

      if (!normalizedProduct) itemErrors.product = 'Product is required.';
      if (!quantity) itemErrors.quantity = 'Quantity is required.';
      if (needsDateFields(normalizedReason)) {
        if (!row.manufacturingDate) itemErrors.manufacturingDate = 'Manufacturing date is required.';
        if (!row.expiryDate) itemErrors.expiryDate = 'Expiry date is required.';
      }

      if (Object.keys(itemErrors).length) {
        nextErrors[row.id] = itemErrors;
      }

      return {
        id: row.id,
        productId: normalizeText(row.productId),
        productName: normalizedProduct,
        sku: normalizeText(row.sku),
        quantity,
        reason: normalizedReason,
        manufacturingDate: needsDateFields(normalizedReason) ? normalizeText(row.manufacturingDate) : '',
        expiryDate: needsDateFields(normalizedReason) ? normalizeText(row.expiryDate) : '',
      };
    }).filter((item) => item.productName || item.quantity);

    if (!trimmedStoreInfoId || !trimmedVendorId || !trimmedStore) nextErrors.storeName = 'Store name is required.';
    if (!trimmedSeller) nextErrors.sellerName = 'Seller is required.';
    if (!items.length) nextErrors.rows = 'Add at least one product.';

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length) return null;

    return {
      id: editingRecordId || `return-${Date.now()}`,
      storeId: trimmedStoreInfoId,
      storeInfoId: trimmedStoreInfoId,
      vendorId: trimmedVendorId,
      legacyStoreId: selectedStoreContext.legacyStoreId || null,
      transferName,
      storeName: trimmedStore,
      sellerName: trimmedSeller,
      items,
      createdAt: currentEditingRecord?.createdAt || new Date().toISOString(),
      createdBy: currentEditingRecord?.createdBy || user?.name || 'Current User',
      updatedAt: new Date().toISOString(),
      updatedBy: user?.name || 'Current User',
    };
  };

  const persistReturn = async (status) => {
    const payload = buildPayload();
    if (!payload) {
      toast.error('Please fix the highlighted fields before saving.');
      return;
    }

    const duplicateRecord = records.find((record) => (
      record.transferName === payload.transferName
      && record.status === status
      && record.id !== payload.id
    ));

    let overwriteId = '';
    if (duplicateRecord) {
      const shouldOverwrite = window.confirm(`Transfer ${payload.transferName} already exists. Overwrite it?`);
      if (!shouldOverwrite) return;
      overwriteId = duplicateRecord.id;
    }

    try {
      const response = await productReturnsAPI.save({
        ...payload,
        id: overwriteId || payload.id,
        status,
        timestamp: new Date().toISOString(),
      });
      const savedEntry = response.data?.data;

      await loadRecords();
      setEditingRecordId(status === 'Completed' ? '' : (savedEntry?.id || overwriteId || payload.id));
      toast.success(status === 'Completed' ? 'Return marked as completed.' : 'Return saved as incomplete.');

      if (status === 'Completed') {
        resetForm();
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save product return entry');
    }
  };

  const loadForEdit = (record) => {
    setSellerName(record.sellerName);
    setRows(record.items.length ? record.items.map(toRowFromItem) : [createReturnRow()]);
    setEditingRecordId(record.id);
    setErrors({});
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const confirmRecord = async (record) => {
    if (!record?.id || isConfirmedStatus(record.status)) return;
    try {
      await productReturnsAPI.save({
        id: record.id,
        sourceReportId: record.sourceReportId || '',
        storeId: record.storeInfoId || record.storeId,
        storeInfoId: record.storeInfoId || record.storeId,
        vendorId: record.vendorId || selectedStoreContext.vendorId,
        legacyStoreId: record.legacyStoreId || selectedStoreContext.legacyStoreId || null,
        storeName: record.storeName,
        sellerName: record.sellerName,
        transferName: record.transferName,
        items: record.items || [],
        status: 'Completed',
        timestamp: new Date().toISOString(),
        createdAt: record.createdAt || new Date().toISOString(),
        createdBy: record.createdBy || user?.name || 'Current User',
        updatedBy: user?.name || 'Current User',
      });
      await loadRecords();
      toast.success('Return confirmed successfully.');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to confirm return entry');
    }
  };
  const requestEditUnlock = async (record) => {
    if (!record?.id) return;
    setRecords((current) => current.map((entry) => (
      entry.id === record.id
        ? {
            ...entry,
            editControl: {
              ...(entry.editControl || {}),
              unlockRequested: true,
            },
          }
        : entry
    )));
    try {
      await productReturnsAPI.save({
        id: record.id,
        requestUnlock: true,
      });
      await loadRecords();
      toast.success('Edit unlock request sent to admin');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to request edit unlock');
    }
  };

  const closeDeleteConfirm = () => {
    if (deletingRecordId) return;
    setRecordPendingDelete(null);
  };

  const deleteSavedRecord = async () => {
    if (!recordPendingDelete?.id) return;

    setDeletingRecordId(recordPendingDelete.id);
    try {
      await productReturnsAPI.delete(recordPendingDelete.id);
      if (editingRecordId === recordPendingDelete.id) {
        resetForm();
      }
      setRecordPendingDelete(null);
      await loadRecords();
      toast.success('Product return entry deleted.');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to delete product return entry');
    } finally {
      setDeletingRecordId('');
    }
  };

  if (!canViewPage) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Product Returns</h1>
        <div className="card">
          <p className="text-gray-500">Access denied.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1880px] space-y-5 px-5">
      <div className="border-b border-slate-200 pb-3">
        <h1 className="text-[24px] font-bold tracking-tight text-slate-900">Product Return Management</h1>
        <p className="mt-1 text-sm text-slate-600">
          Record seller returns quickly, keep drafts incomplete when needed, and finish completed transfers in one clean workflow.
        </p>
      </div>

      <div className="space-y-4">
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_260px] lg:items-start">
          <div className="rounded-[16px] border border-slate-200 bg-white p-5 shadow-[0_14px_40px_rgba(15,23,42,0.05)]">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Store Entry</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Store is taken from the header selector. Choose seller first; transfer name is generated automatically.
                </p>
              </div>
              <span className="inline-flex w-fit rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                {editingRecordId ? 'Editing Return' : 'New Return'}
              </span>
            </div>

            <div className="mt-4 grid gap-4 lg:grid-cols-2">
              <label className="block">
                <span className="label">Store Name</span>
                <input
                  className="input rounded-lg bg-slate-50 font-semibold text-slate-700"
                  type="text"
                  value={selectedStoreContext.storeName || 'Select a store from the header'}
                  readOnly
                />
                {errors.storeName ? <span className="mt-1 block text-xs text-danger-600">{errors.storeName}</span> : null}
              </label>

              <div>
                <FilterSelect
                  label="Seller"
                  value={sellerName || ''}
                  options={[
                    {
                      value: '',
                      label: !hasStoreContext
                        ? 'Select store from header first'
                        : loadingCatalog
                          ? 'Loading sellers...'
                          : 'Select seller',
                    },
                    ...sellerOptions.map((seller) => ({ value: seller, label: seller })),
                  ]}
                  onChange={setSellerName}
                  className="w-full"
                  disabled={!hasStoreContext}
                />
                {errors.sellerName ? <span className="mt-1 block text-xs text-danger-600">{errors.sellerName}</span> : null}
              </div>

              <label className="block lg:col-span-2">
                <span className="label">Transfer Name</span>
                <input
                  className="input rounded-lg bg-slate-50 font-semibold text-slate-700"
                  type="text"
                  value={transferName}
                  readOnly
                />
                <span className="mt-1 block text-xs text-slate-500">
                  Auto-generated with store code, seller initials, and the next incremental number.
                </span>
              </label>
            </div>
          </div>

          <div className="rounded-[12px] border border-slate-200 bg-slate-50 p-3.5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Live Summary</p>
            <div className="mt-2.5 grid gap-2">
              <div className="rounded-lg bg-white px-3 py-2.5">
                <p className="text-[11px] uppercase tracking-[0.16em] text-slate-500">Current Status</p>
                <p className="mt-1 text-sm font-bold text-slate-900">{editingRecordId ? 'Editing Return' : 'New Return'}</p>
              </div>
              <div className="rounded-lg bg-amber-50 px-3 py-2.5">
                <p className="text-[11px] uppercase tracking-[0.16em] text-amber-600">SKU Count</p>
                <p className="mt-1 text-sm font-bold text-amber-900">{skuCount}</p>
              </div>
              <div className="rounded-lg bg-emerald-50 px-3 py-2.5">
                <p className="text-[11px] uppercase tracking-[0.16em] text-emerald-600">Total Quantity</p>
                <p className="mt-1 text-sm font-bold text-emerald-900">{totalQuantity}</p>
              </div>
            </div>
          </div>
        </div>

          <div className="rounded-[16px] border border-slate-200 bg-white p-8 shadow-[0_14px_40px_rgba(15,23,42,0.05)]">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Product Entry</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Add as many products as needed, save first, then confirm from the logs section.
                </p>
              </div>
              <button
                type="button"
                onClick={addRow}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
              >
                <Plus className="h-4 w-4" />
                Add Product
              </button>
            </div>

            <div className="mt-4 overflow-visible rounded-[14px] border border-slate-200 bg-slate-50/60">
              <div className="hidden grid-cols-[minmax(0,3fr)_minmax(0,0.8fr)_minmax(0,1.1fr)_minmax(0,1fr)_minmax(0,1fr)] gap-3 border-b border-slate-200 bg-white/70 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400 lg:grid">
                <span>Product</span>
                <span>Quantity</span>
                <span>Reason</span>
                <span>MFG Date</span>
                <span>Expiry Date</span>
              </div>

              {rows.map((row, index) => (
                <div
                  key={row.id}
                  style={{ zIndex: rows.length - index }}
                  className={`relative grid gap-2 px-4 py-2 lg:grid-cols-[minmax(0,3fr)_minmax(0,0.8fr)_minmax(0,1.1fr)_minmax(0,1fr)_minmax(0,1fr)] lg:items-center ${
                    index !== rows.length - 1 ? 'border-b border-slate-200' : ''
                  }`}
                >
                  <label className="block min-w-0">
                    <span className="label lg:hidden">Product</span>
                    <SearchableProductInput
                      value={row.productInput}
                      products={filteredProducts}
                      loading={loadingCatalog}
                      placeholder={loadingCatalog ? 'Loading products...' : 'Search product name'}
                      onChangeText={(value) => syncRowProduct(row.id, value)}
                      onSelectProduct={(product) => selectRowProduct(row.id, product)}
                    />
                    {row.sku ? (
                      <div className="mt-0.5 text-[10px] text-slate-500">
                        <span className="truncate">{`SKU: ${row.sku}`}</span>
                      </div>
                    ) : null}
                    {rows.length > 1 ? (
                      <button
                        type="button"
                        onClick={() => removeRow(row.id)}
                        className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 transition hover:text-danger-600"
                        aria-label={`Remove row ${index + 1}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        Remove
                      </button>
                    ) : null}
                    {errors[row.id]?.product ? <span className="mt-1 block text-xs text-danger-600">{errors[row.id].product}</span> : null}
                  </label>

                  <label className="block min-w-0">
                    <span className="label lg:hidden">Quantity</span>
                    <input
                      className="input rounded-lg"
                      inputMode="numeric"
                      placeholder="0"
                      value={row.quantity}
                      onChange={(event) => updateRow(row.id, { quantity: String(event.target.value).replace(/[^0-9]/g, '') })}
                    />
                    {errors[row.id]?.quantity ? <span className="mt-1 block text-xs text-danger-600">{errors[row.id].quantity}</span> : null}
                  </label>

                  <div className="min-w-0">
                    <FilterSelect
                      label=""
                      value={row.reason}
                      options={REASON_OPTIONS}
                      menuPlacement={index === rows.length - 1 ? 'top' : 'bottom'}
                      onChange={(value) => updateRow(row.id, {
                        reason: value,
                        ...(needsDateFields(value) ? {} : { manufacturingDate: '', expiryDate: '' }),
                      })}
                      className="w-full"
                    />
                  </div>

                  <label className="block min-w-0">
                    <span className="label lg:hidden">Manufacturing Date</span>
                    <input
                      className={`input rounded-lg ${needsDateFields(row.reason) ? '' : 'cursor-not-allowed bg-slate-100 text-slate-400'}`}
                      type="date"
                      value={row.manufacturingDate}
                      disabled={!needsDateFields(row.reason)}
                      onChange={(event) => updateRow(row.id, { manufacturingDate: event.target.value })}
                    />
                    {errors[row.id]?.manufacturingDate ? <span className="mt-1 block text-xs text-danger-600">{errors[row.id].manufacturingDate}</span> : null}
                  </label>

                  <label className="block min-w-0">
                    <span className="label lg:hidden">Expiry Date</span>
                    <input
                      className={`input rounded-lg ${needsDateFields(row.reason) ? '' : 'cursor-not-allowed bg-slate-100 text-slate-400'}`}
                      type="date"
                      value={row.expiryDate}
                      disabled={!needsDateFields(row.reason)}
                      onChange={(event) => updateRow(row.id, { expiryDate: event.target.value })}
                    />
                    {errors[row.id]?.expiryDate ? <span className="mt-1 block text-xs text-danger-600">{errors[row.id].expiryDate}</span> : null}
                  </label>

                </div>
              ))}
            </div>

            {errors.rows ? <p className="mt-3 text-sm text-danger-600">{errors.rows}</p> : null}

            <div className="mt-5 flex flex-wrap justify-end gap-3">
              <button
                type="button"
                onClick={() => persistReturn('Incomplete')}
                className="inline-flex items-center gap-2 rounded-lg bg-amber-500 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-amber-500/20 transition hover:bg-amber-400"
              >
                <Save className="h-4 w-4" />
                Save
              </button>
            </div>
          </div>
      </div>

      <div className="rounded-[14px] border border-slate-200 bg-white p-4 shadow-[0_14px_40px_rgba(15,23,42,0.05)]">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">Confirmation Section</p>
            <h2 className="mt-1 text-xl font-bold text-slate-900">Saved entries ready for edit and confirm</h2>
            <p className="mt-1 text-xs text-slate-500">Showing the last 7 days by default. Adjust filters to narrow results.</p>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <label className="block min-w-[140px]">
              <span className="label">From</span>
              <input
                type="date"
                className="input rounded-lg"
                value={savedFromDate}
                max={savedToDate}
                onChange={(event) => setSavedFromDate(event.target.value || defaultSavedEntriesFromDate())}
              />
            </label>
            <label className="block min-w-[140px]">
              <span className="label">To</span>
              <input
                type="date"
                className="input rounded-lg"
                value={savedToDate}
                min={savedFromDate}
                max={todayKey()}
                onChange={(event) => setSavedToDate(event.target.value || todayKey())}
              />
            </label>
            <label className="relative block min-w-[220px]">
              <span className="label">Search</span>
              <Search className="pointer-events-none absolute left-3 top-[2.55rem] h-4 w-4 text-gray-400" />
              <input
                className="input rounded-lg pl-10"
                placeholder="Transfer, seller, product..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </label>
            <FilterSelect
              label="Status"
              value={statusFilter}
              options={STATUS_OPTIONS}
              onChange={setStatusFilter}
              className="w-[180px]"
            />
          </div>
        </div>
        <div className="mt-3 w-fit rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-600">
          {filteredLogs.length} entr{filteredLogs.length === 1 ? 'y' : 'ies'} in range
        </div>

        <div className="mt-4 table-scroll-shell rounded-[14px] border border-slate-200">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3 font-semibold">Transfer Name</th>
                <th className="px-4 py-3 font-semibold">Seller</th>
                <th className="px-4 py-3 font-semibold">Products</th>
                <th className="px-4 py-3 font-semibold text-right">Total Qty</th>
                <th className="px-4 py-3 font-semibold">Saved At</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {loadingRecords ? (
                <tr>
                  <td colSpan="7" className="px-4 py-10 text-center text-sm text-slate-500">
                    Loading product return logs...
                  </td>
                </tr>
              ) : null}
              {!loadingRecords && filteredLogs.map((record) => {
                const locked = isEditLocked(record);
                const unlockRequested = Boolean(record.editControl?.unlockRequested);
                return (
                  <tr key={record.id} className="hover:bg-slate-50/70">
                    <td className="px-4 py-3 font-semibold text-slate-900">{record.transferName}</td>
                    <td className="px-4 py-3 text-slate-700">{record.sellerName || '-'}</td>
                    <td className="px-4 py-3 text-slate-700">
                      <div className="font-medium text-slate-900">{formatProductNamesSummary(record)}</div>
                      <div className="mt-0.5 text-xs text-slate-500">{record.items?.length || record.skuCount || 0} product row(s)</div>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-800">{record.totalQuantity}</td>
                    <td className="px-4 py-3 text-slate-500">{format(new Date(record.timestamp), 'PPpp')}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${
                        isConfirmedStatus(record.status)
                          ? 'border-emerald-200 bg-emerald-100 text-emerald-700'
                          : 'border-sky-200 bg-sky-100 text-sky-700'
                      }`}>
                        {getDisplayStatus(record.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex flex-wrap justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => loadForEdit(record)}
                          disabled={locked}
                          className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                          Edit Again
                        </button>
                        {locked && !unlockRequested ? (
                          <button
                            type="button"
                            onClick={() => requestEditUnlock(record)}
                            className="inline-flex items-center gap-1.5 rounded-xl bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-amber-500"
                          >
                            Request Admin Edit
                          </button>
                        ) : null}
                        {locked && unlockRequested ? (
                          <button
                            type="button"
                            disabled
                            className="inline-flex items-center gap-1.5 rounded-xl bg-slate-400 px-3 py-1.5 text-xs font-semibold text-white"
                          >
                            Requested
                          </button>
                        ) : null}
                        {!locked ? (
                          <button
                            type="button"
                            onClick={() => setRecordPendingDelete(record)}
                            className="inline-flex items-center gap-1.5 rounded-xl border border-rose-200 bg-white px-3 py-1.5 text-xs font-semibold text-rose-600 transition hover:bg-rose-50"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            Delete
                          </button>
                        ) : null}
                        
                        <button
                          type="button"
                          onClick={() => confirmRecord(record)}
                          disabled={isConfirmedStatus(record.status)}
                          className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-60 ${
                            isConfirmedStatus(record.status) ? 'bg-slate-400' : 'bg-emerald-600 hover:bg-emerald-500'
                          }`}
                        >
                          <Save className="h-3.5 w-3.5" />
                          {isConfirmedStatus(record.status) ? 'Confirmed' : 'Confirm'}
                        </button>
                        <ExportButton size="sm" onClick={() => exportProductReturnEntry(record)} />
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!loadingRecords && filteredLogs.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-10 text-center text-sm text-slate-500">
                    <FileSearch className="mx-auto h-8 w-8 text-slate-300" />
                    <p className="mt-3">No product return logs match the current search and filter.</p>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmModal
        isOpen={Boolean(recordPendingDelete)}
        title="Delete Product Return?"
        message={(
          <p>
            This will delete{' '}
            <span className="font-semibold text-gray-900">
              {recordPendingDelete?.transferName || 'this saved product return'}
            </span>
            {' '}and its product rows. This cannot be undone.
          </p>
        )}
        confirmLabel="Delete"
        cancelLabel="Keep Entry"
        variant="danger"
        loading={Boolean(deletingRecordId)}
        onClose={closeDeleteConfirm}
        onConfirm={deleteSavedRecord}
      />
    </div>
  );
};

export default ProductReturnsTask;
